import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import { useCustomers, useCreateCustomer } from '@/data/customers';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { TableScroll } from '@/components/ui/table-scroll';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import type { CreateCustomerBody } from '@breaker/shared-schemas';

export function CustomersPage() {
  const { fleet } = useActiveFleet();
  const fleetId = fleet?.id ?? null;
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  // M2a: BR110 refuses to issue a VAT invoice for a customer with no billing
  // address, so the marker exists to surface that BEFORE anyone is trying to
  // bill — and the filter is how an owner clears the backlog in one pass
  // rather than meeting it one refusal at a time.
  const [billingOnly, setBillingOnly] = useState(false);
  const customersQuery = useCustomers(fleetId, {
    search,
    ...(billingOnly ? { billingIncomplete: true } : {}),
    limit: 100,
  });
  const incompleteCount = (customersQuery.data ?? []).filter(
    (c) => (c.billingMissingFields ?? []).length > 0,
  ).length;

  return (
    <div className="px-8 py-10 max-w-[1100px]">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">
            Customers
          </h1>
          <p className="text-sm text-breaker-mute mt-1">
            Click a customer to see their load history.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          New customer
        </Button>
      </header>

      <div className="mb-5 flex items-end gap-4 flex-wrap">
        <div className="max-w-sm flex-1 min-w-[220px]">
          <Field label="Search" htmlFor="customerSearch">
            <Input
              id="customerSearch"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name"
              autoComplete="off"
            />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-breaker-ink-2 pb-2">
          <input
            type="checkbox"
            checked={billingOnly}
            onChange={(e) => setBillingOnly(e.target.checked)}
          />
          Billing incomplete only
          {!billingOnly && incompleteCount > 0 ? (
            <span className="text-breaker-mute">({incompleteCount} on this page)</span>
          ) : null}
        </label>
      </div>

      {customersQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : customersQuery.isError ? (
        <EmptyState
          title="Could not load customers"
          description={(customersQuery.error as Error)?.message ?? 'Server error.'}
        />
      ) : (customersQuery.data ?? []).length === 0 ? (
        <EmptyState
          title={search ? 'No customers match' : 'No customers yet'}
          description={
            search
              ? 'Try a different search term.'
              : 'Create one with the New customer button, or assign one on a load.'
          }
        />
      ) : (
        <div className="surface rounded-lg overflow-hidden">
          <TableScroll>
          <table className="w-full text-sm">
            <thead className="bg-breaker-ground-2 border-b border-breaker-line">
              <tr className="text-left">
                <Th>Name</Th>
                <Th className="w-[200px]">Account ref</Th>
                <Th>Email</Th>
                <Th className="w-[150px]">Billing</Th>
              </tr>
            </thead>
            <tbody>
              {(customersQuery.data ?? []).map((c) => (
                <tr
                  key={c.id}
                  className="border-b last:border-b-0 border-breaker-line hover:bg-breaker-ground-2 transition-colors"
                >
                  <Td>
                    <Link
                      to="/customers/$customerId"
                      params={{ customerId: c.id }}
                      className="font-medium text-breaker-ink hover:text-breaker-identity transition-colors"
                    >
                      {c.name}
                    </Link>
                  </Td>
                  <Td className="text-breaker-ink-2">
                    {c.accountRef ?? <span className="text-breaker-mute">{'\u2014'}</span>}
                  </Td>
                  <Td className="text-breaker-ink-2">
                    {c.primaryEmail ?? <span className="text-breaker-mute">{'\u2014'}</span>}
                  </Td>
                  <Td>
                    {(c.billingMissingFields ?? []).length > 0 ? (
                      <Badge tone="active">Billing incomplete</Badge>
                    ) : (
                      <span className="text-breaker-mute">{'\u2014'}</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
        </div>
      )}

      <NewCustomerDialog
        open={creating}
        fleetId={fleetId}
        onClose={() => setCreating(false)}
      />
    </div>
  );
}

function NewCustomerDialog({
  open,
  fleetId,
  onClose,
}: {
  open: boolean;
  fleetId: string | null;
  onClose: () => void;
}) {
  const create = useCreateCustomer(fleetId);
  const [name, setName] = useState('');
  const [accountRef, setAccountRef] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  // Billing block. Optional by design — a dispatcher booking a same-day job
  // must never be blocked here. But it is on the CREATE form because an
  // invoice freezes these details at issue: a customer created without them
  // and invoiced the same day carries a permanently incomplete document, and
  // the only remedy is a credit note. Cheap to capture now, impossible to
  // correct later.
  const [showBilling, setShowBilling] = useState(false);
  const [vatNumber, setVatNumber] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [billingLine1, setBillingLine1] = useState('');
  const [billingLine2, setBillingLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingPostcode, setBillingPostcode] = useState('');
  const [billingCountry, setBillingCountry] = useState('GB');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName('');
    setAccountRef('');
    setEmail('');
    setPhone('');
    setNotes('');
    setShowBilling(false);
    setVatNumber('');
    setBillingEmail('');
    setBillingLine1('');
    setBillingLine2('');
    setBillingCity('');
    setBillingPostcode('');
    setBillingCountry('GB');
    setError(null);
  }

  function close() {
    reset();
    onClose();
  }

  function onSubmit() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return Promise.resolve();
    }
    setError(null);
    const body: CreateCustomerBody = { name: trimmedName };
    if (accountRef.trim()) body.accountRef = accountRef.trim();
    if (email.trim()) body.primaryEmail = email.trim();
    if (phone.trim()) body.primaryPhoneE164 = phone.trim();
    if (notes.trim()) body.notes = notes.trim();
    if (vatNumber.trim()) body.vatNumber = vatNumber.trim();
    if (billingEmail.trim()) body.billingEmail = billingEmail.trim();
    if (billingLine1.trim()) body.billingAddressLine1 = billingLine1.trim();
    if (billingLine2.trim()) body.billingAddressLine2 = billingLine2.trim();
    if (billingCity.trim()) body.billingCity = billingCity.trim();
    if (billingPostcode.trim()) body.billingPostcode = billingPostcode.trim();
    // Sent as typed; the schema upper-cases it. Deliberately NOT normalised
    // here as well — one definition of the rule, on the server, shared by the
    // create and edit paths.
    if (billingCountry.trim()) body.billingCountry = billingCountry.trim();
    return create
      .mutateAsync(body)
      .then(() => close())
      .catch((e: unknown) => {
        setError((e as Error)?.message ?? 'Could not create customer.');
      });
  }

  return (
    <Dialog open={open} onClose={close} title="New customer">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name" htmlFor="nName">
          <Input
            id="nName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Account ref" htmlFor="nAccount">
          <Input
            id="nAccount"
            value={accountRef}
            onChange={(e) => setAccountRef(e.target.value)}
          />
        </Field>
        <Field label="Email" htmlFor="nEmail">
          <Input
            id="nEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Phone (E.164)" htmlFor="nPhone">
          <Input
            id="nPhone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+447700900123"
          />
        </Field>
      </div>
      <div className="mt-4">
        <Field label="Notes" htmlFor="nNotes">
          <Textarea
            id="nNotes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </div>
      <div className="mt-4 border-t border-breaker-line-2 pt-4">
        <button
          type="button"
          className="text-sm text-breaker-ink-2 hover:text-breaker-ink"
          onClick={() => setShowBilling((v) => !v)}
          aria-expanded={showBilling}
        >
          {showBilling ? '−' : '+'} Billing details for invoices{' '}
          <span className="text-breaker-mute">(optional)</span>
        </button>
        {showBilling ? (
          <>
            <p className="text-xs text-breaker-mute mt-2">
              Copied onto their invoices when you issue one. An issued invoice
              can’t be edited afterwards, so it’s worth filling in before the
              first job is billed.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <Field label="VAT number" htmlFor="nVat">
                <Input
                  id="nVat"
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  placeholder="GB123456789"
                />
              </Field>
              <Field label="Billing email" htmlFor="nBillEmail">
                <Input
                  id="nBillEmail"
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  placeholder="accounts@customer.example"
                />
              </Field>
              <Field label="Billing address" htmlFor="nBillLine1">
                <Input
                  id="nBillLine1"
                  value={billingLine1}
                  onChange={(e) => setBillingLine1(e.target.value)}
                />
              </Field>
              <Field label="Address line 2" htmlFor="nBillLine2">
                <Input
                  id="nBillLine2"
                  value={billingLine2}
                  onChange={(e) => setBillingLine2(e.target.value)}
                />
              </Field>
              <Field label="Town or city" htmlFor="nBillCity">
                <Input
                  id="nBillCity"
                  value={billingCity}
                  onChange={(e) => setBillingCity(e.target.value)}
                />
              </Field>
              <Field label="Postcode" htmlFor="nBillPostcode">
                <Input
                  id="nBillPostcode"
                  value={billingPostcode}
                  onChange={(e) => setBillingPostcode(e.target.value)}
                />
              </Field>
              <Field label="Country" htmlFor="nBillCountry">
                <Input
                  id="nBillCountry"
                  value={billingCountry}
                  onChange={(e) => setBillingCountry(e.target.value)}
                  placeholder="GB"
                  maxLength={2}
                />
              </Field>
            </div>
          </>
        ) : null}
      </div>
      {error ? <p className="text-xs text-breaker-red mt-3">{error}</p> : null}
      <div className="mt-5 flex justify-end gap-2">
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={close}
          disabled={create.isPending}
        >
          Cancel
        </Button>
        <Button size="sm" type="button" action={onSubmit} loading={create.isPending}>
          Create
        </Button>
      </div>
    </Dialog>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'px-4 py-2.5 text-2xs font-medium uppercase tracking-wider text-breaker-ink-2',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 align-middle', className)}>{children}</td>;
}
